import { useParams } from 'next/navigation';
import { useState, useEffect } from 'react';
import { WALLET_PHRASES } from '@/consts/phrases/walletPhrases';
import { NonMemberPaymentDetailsForm } from '@/components/forms/forms/wallet/non-member/NonMemberPaymentDetailsForm/NonMemberPaymentDetailsForm';
import { NonMemberPaymentDetailsFormSkeleton } from '@/components/forms/forms/wallet/non-member/NonMemberPaymentDetailsForm/NonMemberPaymentDetailsFormSkeleton';
import { NonMemberSummaryDetails } from '@/components/blocks/wallet/non-member/NonMemberSummaryDetails/NonMemberSummaryDetails';
import NonMemberTransactionTable from '@/components/blocks/wallet/non-member/NonMemberTransactionTable/NonMemberTransactionTable';
import { TransactionHistoryModal } from '@/components/blocks/wallet/Transactions/TransactionTableSection/modals';
import { DefaultPageProps } from '@/types/types';
import { Grid, Skeleton, Typography } from '@mui/material';
import { getAccountLocale, JSXFromString } from '@/utils';
import { Query } from '@/components/layout/Query/Query';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import { useNonMemberDataQuery } from '@/hooks/queries/wallet/walletQueries';
import { useSwitchLocale } from '@/hooks/general/useSwitchLocale/useSwitchLocale';
import { useLangSwitcherStore } from '@/hooks/helpers/useIsLangSwitcherDisabled/langSwitcherStore';

interface ModalState {
    isOpen: boolean;
    token: string | undefined;
    transactionId: number | undefined;
}

interface NonMemberPaymentProps {
    dictionary: DefaultPageProps['dictionary'];
}

export const NonMemberPayment = ({ dictionary }: NonMemberPaymentProps) => {
    const { t } = usePhrase();

    const { token } = useParams<{ token: string }>();

    const [paymentMethodId, setPaymentMethodId] = useState<number | undefined>();
    const { data, isLoading, error } = useNonMemberDataQuery(token, paymentMethodId);

    const { countryCode, preferredLanguageId } = data || {};
    const isValidLocale = getAccountLocale(preferredLanguageId, countryCode, dictionary.countries);
    const hasLocaleData = Boolean(preferredLanguageId && isValidLocale);

    useEffect(() => {
        const { setForceEnabled } = useLangSwitcherStore.getState();

        if (data) {
            setForceEnabled(!hasLocaleData);
        }

        return () => {
            setForceEnabled(false);
        };
    }, [countryCode, preferredLanguageId, data, hasLocaleData]);

    useSwitchLocale(preferredLanguageId, countryCode, dictionary.countries, !hasLocaleData);

    const [modalState, setModalState] = useState<ModalState>({
        isOpen: false,
        token: undefined,
        transactionId: undefined,
    });

    const handleTransactionHistoryModalOpen = (token: string) => {
        const transactionId = data?.payments.find(
            (payment) => payment.token === token,
        )?.transactionId;

        setModalState({
            isOpen: true,
            token,
            transactionId,
        });
    };

    const handleModalClose = () =>
        setModalState((state) => ({
            ...state,
            isOpen: false,
        }));

    const firstTransaction = data?.payments?.[0];

    return (
        <>
            <Query
                data={data}
                error={error}
                isLoading={isLoading}
                loaderComponent={
                    <Grid container>
                        <Grid size={12}>
                            <Skeleton data-testid="skeleton" height={150} />
                            <Skeleton data-testid="skeleton" height={70} />
                        </Grid>
                    </Grid>
                }
            >
                {({ data }) => (
                    <>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            {JSXFromString(t(WALLET_PHRASES.NON_MEMBER_SUBTEXT_PAYMENT_STATUS))}
                        </Typography>
                        {firstTransaction && (
                            <NonMemberTransactionTable
                                transactions={data.payments}
                                currencyCode={firstTransaction.currencyCode}
                                currencySymbol={firstTransaction.currencySymbol}
                                currencySymbolPosition={firstTransaction.currencySymbolPosition}
                                handleTransactionHistoryClicked={handleTransactionHistoryModalOpen}
                                selectedToken={token}
                            />
                        )}
                        {token ? (
                            <>
                                {data.isPaymentEditable ? (
                                    <NonMemberPaymentDetailsForm
                                        data={data}
                                        dictionary={dictionary}
                                        token={token}
                                        setPaymentMethodId={setPaymentMethodId}
                                    />
                                ) : (
                                    <NonMemberSummaryDetails data={data} token={token} />
                                )}
                            </>
                        ) : (
                            <NonMemberPaymentDetailsFormSkeleton />
                        )}
                        <TransactionHistoryModal
                            isOpen={modalState.isOpen}
                            handleClose={handleModalClose}
                            transactionId={modalState.transactionId}
                            token={token}
                        />
                    </>
                )}
            </Query>
        </>
    );
};
